import {messages} from '@smartmixin/i18n';
import {
  alpha,
  Box,
  Chip,
  Divider,
  Link,
  Typography,
  useTheme,
} from '@mui/material';
import {Clock, Desktop, DeviceMobile, LinkSimple} from '@phosphor-icons/react';
import {ReactNode} from 'react';
import {useIntl} from 'react-intl';
import ReactMarkdown, {Components} from 'react-markdown';
import remarkDirective from 'remark-directive';
import remarkGfm from 'remark-gfm';
import {getReadingTime} from '../constants';
import {useArticles} from '../hooks/useArticles';
import {Article} from '../types';
import {platformTabsPlugin} from '../utils/platformTabsPlugin';
import PlatformTabs, {PlatformTab} from './PlatformTabs';
import {
  flattenNodeText,
  HEADING_SCROLL_OFFSET,
  scrollToHeading,
  slugifyHeading,
} from '../utils/headings';
import {wikiLinkPlugin} from '../utils/wikiLinkPlugin';
import ArticleFeedback from './ArticleFeedback';
import ArticleLink from './ArticleLink';
import HelpImage from './HelpImage';
import RelatedArticles from './RelatedArticles';

type ArticleViewProps = {
  article: Article;
};

// Custom component type for article-link
type ArticleLinkComponentProps = {
  slug: string;
  displayText: string;
};

// Extend Components type to include custom help-center elements
type ExtendedComponents = Components & {
  'article-link'?: React.ComponentType<ArticleLinkComponentProps>;
  'platform-tabs'?: React.ComponentType<{children?: ReactNode}>;
  'platform-tab'?: React.ComponentType<{
    platform: 'web' | 'mobile';
    children?: ReactNode;
  }>;
};

type AnchoredHeadingProps = {
  level: 2 | 3;
  children?: ReactNode;
};

// h2/h3 with a stable anchor id (matching extractHeadings) and a
// hover-revealed permalink, so support replies can deep-link to a section.
const AnchoredHeading = ({level, children}: AnchoredHeadingProps) => {
  const {formatMessage} = useIntl();
  const id = slugifyHeading(flattenNodeText(children));

  return (
    <Typography
      id={id || undefined}
      variant={level === 2 ? 'h5' : 'h6'}
      component={`h${level}`}
      gutterBottom
      sx={{
        mt: level === 2 ? 4 : 3,
        fontWeight: 600,
        color: 'text.primary',
        scrollMarginTop: `${HEADING_SCROLL_OFFSET}px`,
        '&:hover .heading-anchor': {opacity: 1},
      }}>
      {children}
      {id && (
        <Link
          href={`#${id}`}
          className="heading-anchor"
          aria-label={formatMessage(messages.common.linkToSection)}
          onClick={(event) => {
            event.preventDefault();
            scrollToHeading(id);
          }}
          sx={{
            ml: 1,
            color: 'text.secondary',
            opacity: {xs: 1, md: 0},
            transition: 'opacity 150ms ease',
            verticalAlign: 'middle',
            '&:hover': {color: 'primary.main'},
          }}>
          <LinkSimple size={16} />
        </Link>
      )}
    </Typography>
  );
};

const ArticleView = ({article}: ArticleViewProps) => {
  const theme = useTheme();
  const {formatMessage} = useIntl();
  const {getRelatedArticles} = useArticles();
  const relatedArticles = getRelatedArticles(article);
  const readingTime = getReadingTime(article.content);

  const components: ExtendedComponents = {
    h1: ({children}) => (
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        sx={{fontWeight: 700, color: 'text.primary'}}>
        {children}
      </Typography>
    ),
    h2: ({children}) => <AnchoredHeading level={2}>{children}</AnchoredHeading>,
    h3: ({children}) => <AnchoredHeading level={3}>{children}</AnchoredHeading>,
    p: ({children}) => (
      <Typography
        component="p"
        sx={{mb: 2, lineHeight: 1.8, color: 'text.secondary'}}>
        {children}
      </Typography>
    ),
    ul: ({children}) => (
      <Box component="ul" sx={{pl: 3, mb: 2}}>
        {children}
      </Box>
    ),
    ol: ({children}) => (
      <Box component="ol" sx={{pl: 3, mb: 2}}>
        {children}
      </Box>
    ),
    li: ({children}) => (
      <Typography
        component="li"
        sx={{mb: 0.5, lineHeight: 1.7, color: 'text.secondary'}}>
        {children}
      </Typography>
    ),
    code: ({children, className}) => {
      const isBlock = className?.includes('language-');
      if (isBlock) {
        return (
          <Box
            component="pre"
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              p: 2,
              borderRadius: 2,
              overflow: 'auto',
              mb: 2,
              border: 1,
              borderColor: 'divider',
            }}>
            <code>{children}</code>
          </Box>
        );
      }
      return (
        <Box
          component="code"
          sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: 'primary.main',
            px: 0.75,
            py: 0.25,
            borderRadius: 1,
            fontSize: '0.875em',
            fontWeight: 500,
          }}>
          {children}
        </Box>
      );
    },
    blockquote: ({children}) => (
      <Box
        component="blockquote"
        sx={{
          borderLeft: 4,
          borderColor: 'primary.main',
          pl: 2,
          py: 1,
          my: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          borderRadius: '0 8px 8px 0',
        }}>
        {children}
      </Box>
    ),
    table: ({children}) => (
      <Box
        component="table"
        sx={{
          width: '100%',
          borderCollapse: 'collapse',
          mb: 3,
          mt: 1,
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
        }}>
        {children}
      </Box>
    ),
    thead: ({children}) => (
      <Box
        component="thead"
        sx={{bgcolor: alpha(theme.palette.primary.main, 0.08)}}>
        {children}
      </Box>
    ),
    th: ({children}) => (
      <Box
        component="th"
        sx={{
          p: 1.5,
          textAlign: 'left',
          fontWeight: 600,
          fontSize: '0.875rem',
          color: 'text.primary',
          borderBottom: 1,
          borderColor: 'divider',
        }}>
        {children}
      </Box>
    ),
    td: ({children}) => (
      <Box
        component="td"
        sx={{
          p: 1.5,
          fontSize: '0.875rem',
          color: 'text.secondary',
          borderBottom: 1,
          borderColor: 'divider',
          lineHeight: 1.6,
        }}>
        {children}
      </Box>
    ),
    // Screenshots/illustrations (see HelpImage for the path conventions)
    img: ({src, alt}) => (
      <HelpImage src={typeof src === 'string' ? src : undefined} alt={alt} />
    ),
    // Custom component for wiki-links
    'article-link': ({slug, displayText}: ArticleLinkComponentProps) => (
      <ArticleLink slug={slug} displayText={displayText} />
    ),
    // Synced web/mobile tab groups (:::web / :::mobile directives)
    'platform-tabs': PlatformTabs,
    'platform-tab': PlatformTab,
  };

  return (
    <Box>
      {/* Article metadata */}
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 3}}>
        <Chip
          icon={<Clock size={16} />}
          label={formatMessage(messages.common.minRead, {
            minutes: readingTime,
          })}
          size="small"
          variant="outlined"
          sx={{
            borderColor: 'divider',
            '& .MuiChip-icon': {
              color: 'text.secondary',
            },
          }}
        />
        {/* Availability badge for single-platform features */}
        {article.platforms?.length === 1 && (
          <Chip
            icon={
              article.platforms[0] === 'mobile' ? (
                <DeviceMobile size={16} />
              ) : (
                <Desktop size={16} />
              )
            }
            label={formatMessage(
              article.platforms[0] === 'mobile'
                ? messages.common.platformMobileOnly
                : messages.common.platformWebOnly,
            )}
            size="small"
            color="primary"
            variant="outlined"
          />
        )}
      </Box>

      {/* Article content */}
      <ReactMarkdown
        remarkPlugins={[
          remarkGfm,
          remarkDirective,
          platformTabsPlugin,
          wikiLinkPlugin,
        ]}
        components={components as Components}>
        {article.content}
      </ReactMarkdown>

      {/* Feedback section */}
      <ArticleFeedback articleId={article.id} />

      {/* Divider before related articles */}
      {relatedArticles.length > 0 && <Divider sx={{my: 4}} />}

      {/* Related articles */}
      <RelatedArticles articles={relatedArticles} />
    </Box>
  );
};

export default ArticleView;
